/**
 * Schema.org structured data generators for SEO
 * Used to provide rich search results in Google and other search engines
 * Includes AIO (AI Optimization) schemas for voice search and AI assistants
 * 
 * All constants imported from src/config/seo.ts - Single Source of Truth
 */

import {
  SITE_NAME,
  SITE_URL,
  SITE_LOGO,
  DEFAULT_PUBLICATION_AUTHOR,
  PUBLISHER_SCHEMA,
  getOrigin,
} from '@/config/seo';

// =====================================================
// AIO (AI Optimization) Schemas
// =====================================================

/**
 * Generate SpeakableSpecification for voice search and AI assistants
 * Marks content sections that are suitable for text-to-speech
 */
export const generateSpeakableSchema = (speakable: {
  cssSelectors: string[];
  url?: string;
}) => ({
  "@type": "SpeakableSpecification",
  "cssSelector": speakable.cssSelectors,
  ...(speakable.url && { "url": speakable.url })
});

/**
 * Generate DefinedTerm schema for tax glossary terms
 * Helps AI assistants understand tax terminology
 */
export const generateDefinedTermSchema = (term: {
  name: string;
  description: string;
  url?: string;
  termCode?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "DefinedTerm",
  "name": term.name,
  "description": term.description,
  "inDefinedTermSet": {
    "@type": "DefinedTermSet",
    "name": "Tax.Network Glossary",
    "url": `${SITE_URL}/glossary`
  },
  ...(term.url && { "url": term.url }),
  ...(term.termCode && { "termCode": term.termCode })
});

/**
 * Generate ClaimReview schema for fact-checked tax information
 * Builds trust with search engines for verified tax claims
 */
export const generateClaimReviewSchema = (claim: {
  claimReviewed: string;
  author: string;
  datePublished: string;
  url: string;
  reviewRating: {
    ratingValue: number;
    bestRating?: number;
    worstRating?: number;
    alternateName?: string;
  };
}) => ({
  "@context": "https://schema.org",
  "@type": "ClaimReview",
  "claimReviewed": claim.claimReviewed,
  "author": {
    "@type": "Organization",
    "name": SITE_NAME,
    "url": SITE_URL
  },
  "reviewRating": {
    "@type": "Rating",
    "ratingValue": claim.reviewRating.ratingValue,
    "bestRating": claim.reviewRating.bestRating || 5,
    "worstRating": claim.reviewRating.worstRating || 1,
    ...(claim.reviewRating.alternateName && { "alternateName": claim.reviewRating.alternateName })
  },
  "datePublished": claim.datePublished,
  "url": claim.url
});

// =====================================================
// Core SEO Schemas
// =====================================================

export const generateOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": SITE_NAME,
  "url": SITE_URL,
  "logo": SITE_LOGO,
  "description": "Global network connecting businesses with verified tax professionals and consultants worldwide",
  "sameAs": []
});

export const generateWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": SITE_NAME,
  "url": SITE_URL,
  "description": "Global tax expert network and knowledge platform",
  "publisher": generateOrganizationSchema(),
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${SITE_URL}/experts?search={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
});

/**
 * Enhanced Publication schema with AIO speakable support
 * When author is null/missing, uses "Editorial Team" as Organization type
 */
export const generatePublicationSchema = (publication: {
  title: string;
  description: string;
  publicationBody?: string;
  image?: string;
  url: string;
  datePublished: string;
  dateModified?: string;
  author?: {
    name?: string;
    url?: string;
  } | null;
  speakable?: {
    cssSelectors: string[];
  };
  keywords?: string[];
  wordCount?: number;
}) => {
  const authorName = publication.author?.name || DEFAULT_PUBLICATION_AUTHOR;
  const isEditorialTeam = !publication.author?.name;

  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": publication.title,
    "description": publication.description,
    ...(publication.publicationBody && { "articleBody": publication.publicationBody }),
    "image": publication.image || SITE_LOGO,
    "url": publication.url,
    "datePublished": publication.datePublished,
    "dateModified": publication.dateModified || publication.datePublished,
    "author": isEditorialTeam
      ? {
          "@type": "Organization",
          "name": authorName,
          "url": SITE_URL
        }
      : {
          "@type": "Person",
          "name": authorName,
          "url": publication.author?.url
        },
    "publisher": generateOrganizationSchema(),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": publication.url
    },
    ...(publication.speakable && {
      "speakable": generateSpeakableSchema({ cssSelectors: publication.speakable.cssSelectors })
    }),
    ...(publication.keywords && { "keywords": publication.keywords.join(", ") }),
    ...(publication.wordCount && { "wordCount": publication.wordCount })
  };
};

/**
 * Enhanced Person schema with AIO properties
 */
export const generatePersonSchema = (person: {
  name: string;
  jobTitle?: string;
  description?: string;
  image?: string;
  url: string;
  address?: {
    city?: string;
    state?: string;
    country?: string;
  };
  telephone?: string;
  email?: string;
  worksFor?: string;
  knowsLanguage?: string[];
  speakable?: {
    cssSelectors: string[];
  };
  alumniOf?: string;
  hasCredential?: {
    name: string;
    issuedBy?: string;
  }[];
}) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "name": person.name,
  "jobTitle": person.jobTitle || "Tax Professional",
  "description": person.description,
  "image": person.image,
  "url": person.url,
  "address": person.address ? {
    "@type": "PostalAddress",
    "addressLocality": person.address.city,
    "addressRegion": person.address.state,
    "addressCountry": person.address.country,
  } : undefined,
  "telephone": person.telephone,
  "email": person.email,
  "worksFor": person.worksFor ? {
    "@type": "Organization",
    "name": person.worksFor
  } : undefined,
  ...(person.knowsLanguage && { "knowsLanguage": person.knowsLanguage }),
  ...(person.speakable && {
    "speakable": generateSpeakableSchema({ cssSelectors: person.speakable.cssSelectors })
  }),
  ...(person.alumniOf && { "alumniOf": person.alumniOf }),
  ...(person.hasCredential && {
    "hasCredential": person.hasCredential.map(cred => ({
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "Professional Certification",
      "name": cred.name,
      ...(cred.issuedBy && { "recognizedBy": { "@type": "Organization", "name": cred.issuedBy } })
    }))
  })
});

export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url,
  }))
});

export const generateProfessionalServiceSchema = (service: {
  name: string;
  description: string;
  url: string;
  provider: {
    name: string;
    image?: string;
    telephone?: string;
    address?: {
      city?: string;
      state?: string;
      country?: string;
    };
  };
  areaServed?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "name": service.name,
  "description": service.description,
  "url": service.url,
  "provider": {
    "@type": "Person",
    "name": service.provider.name,
    "image": service.provider.image,
    "telephone": service.provider.telephone,
    "address": service.provider.address ? {
      "@type": "PostalAddress",
      "addressLocality": service.provider.address.city,
      "addressRegion": service.provider.address.state,
      "addressCountry": service.provider.address.country,
    } : undefined,
  },
  "areaServed": service.areaServed,
});

export const generateItemListSchema = (items: Array<{
  name: string;
  url: string;
  description?: string;
  image?: string;
}>) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "item": {
      "@type": "Thing",
      "name": item.name,
      "url": item.url,
      "description": item.description,
      "image": item.image,
    }
  }))
});

/**
 * Generate FAQPage schema for rich snippets
 * Use on support/FAQ pages
 */
export const generateFAQPageSchema = (faqs: Array<{ question: string; answer: string }>) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

/**
 * Generate LocalBusiness schema for location pages
 * Enhances local SEO presence
 */
export const generateLocalBusinessSchema = (location: {
  name: string;
  description: string;
  url: string;
  country: string;
  services?: string[];
}) => ({
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "name": `${SITE_NAME} - ${location.name}`,
  "description": location.description,
  "url": location.url,
  "areaServed": {
    "@type": "Country",
    "name": location.country
  },
  "serviceType": location.services || [
    "Tax Consulting",
    "Tax Planning",
    "International Tax",
    "Corporate Tax",
    "Personal Tax"
  ],
  "provider": generateOrganizationSchema()
});

/**
 * Generate HowTo schema for step-by-step guides
 * Enables rich how-to snippets in search
 */
export const generateHowToSchema = (howTo: {
  name: string;
  description: string;
  steps: Array<{ name: string; text: string; image?: string }>;
  totalTime?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": howTo.name,
  "description": howTo.description,
  "totalTime": howTo.totalTime,
  "step": howTo.steps.map((step, index) => ({
    "@type": "HowToStep",
    "position": index + 1,
    "name": step.name,
    "text": step.text,
    "image": step.image
  }))
});

/**
 * Generate VideoObject schema for video content
 * Enables video rich snippets
 */
export const generateVideoSchema = (video: {
  name: string;
  description: string;
  thumbnailUrl: string;
  uploadDate: string;
  duration?: string;
  contentUrl?: string;
  embedUrl?: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "VideoObject",
  "name": video.name,
  "description": video.description,
  "thumbnailUrl": video.thumbnailUrl,
  "uploadDate": video.uploadDate,
  ...(video.duration && { "duration": video.duration }),
  ...(video.contentUrl && { "contentUrl": video.contentUrl }),
  ...(video.embedUrl && { "embedUrl": video.embedUrl })
});

/**
 * Generate CollectionPage schema for listing pages
 */
export const generateCollectionPageSchema = (collection: {
  name: string;
  description: string;
  url: string;
}) => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": collection.name,
  "description": collection.description,
  "url": collection.url,
  "publisher": PUBLISHER_SCHEMA,
});

// =====================================================
// Semantic CSS IDs for AIO Speakable targeting
// =====================================================
export const SPEAKABLE_SELECTORS = {
  publication: {
    title: '#publication-title',
    excerpt: '#publication-excerpt',
    content: '#publication-content',
    author: '#publication-author'
  },
  expert: {
    name: '#expert-name',
    title: '#expert-title',
    bio: '#expert-bio-summary',
    services: '#expert-services'
  },
  homepage: {
    heading: '#hero-heading',
    subheading: '#hero-subheading'
  },
  location: {
    title: '#location-title',
    description: '#location-description'
  }
};
